import {
	Alert,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	Typography
} from '@mui/material';
import { useRetryVerifyDomain } from 'api/domainVerification';
import Loader from 'assets/animations/Loader';
import DnsRecordInfo from 'components/DomainVerification/DnsRecordInfo/DnsRecordInfo';
import MDRenderer from 'components/MDRenderer/MDRenderer';
import DnsProviderLogo from 'components/shared/DnsProviderLogo';
import { MdComponentNames } from 'consts';
import { useVerifiedDomains } from 'contexts/VerifiedDomainsCtx/VerifiedDomains';
import { useDebouncedDomainProviderGuide } from 'hooks/useDomainProviderGuide';
import React, { useCallback, useEffect } from 'react';
import { IVerifyDomainDialogResult } from 'types/dialogs';

interface IVerifyDomainDialogProps {
	domain: string;
	onClose: (res: IVerifyDomainDialogResult) => void;
}

const mdOverrides = {
	[MdComponentNames.DnsRecordInfo]: DnsRecordInfo
};

const VerifyDomainDialog: React.FC<IVerifyDomainDialogProps> = ({
	domain,
	onClose
}) => {
	const { data: dnsProvider } = useDebouncedDomainProviderGuide(domain);
	const { addVerifiedDomain } = useVerifiedDomains();
	const { data: verifyDomainData } = useRetryVerifyDomain(domain);

	const handleClose = useCallback(() => {
		onClose({
			success: false
		});
	}, [onClose]);

	useEffect(() => {
		if (verifyDomainData?.verified) {
			addVerifiedDomain(domain);
			onClose({
				success: true
			});
		}
	}, [addVerifiedDomain, domain, onClose, verifyDomainData?.verified]);

	return (
		<Dialog open maxWidth="md" fullWidth PaperProps={{ elevation: 0 }}>
			<DialogTitle>
				<Stack direction="row" alignItems="center" gap={2}>
					{dnsProvider ? (
						<>
							<DnsProviderLogo dnsProvider={dnsProvider} />
							{dnsProvider?.guide?.[0].title}
						</>
					) : null}
				</Stack>
			</DialogTitle>
			<DialogContent
				sx={{
					pt: 3
				}}
			>
				<Stack gap={5}>
					<Typography variant="h5">{domain}</Typography>
					{dnsProvider?.guide?.[0].content ? (
						<>
							<MDRenderer
								md={dnsProvider?.guide?.[0].content}
								overrides={mdOverrides}
							/>
							<Alert severity="info">
								{intl.tr('domainVerification.dialog.alert')}
							</Alert>
						</>
					) : (
						<Stack alignItems="center">
							<Loader />
						</Stack>
					)}
				</Stack>
			</DialogContent>
			<DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
				<Stack direction="row" alignItems="center" gap={2}>
					{dnsProvider && (
						<>
							<CircularProgress size={24} />
							<Typography variant="body1">
								{intl.tr('domainVerification.dialog.checkingLoaderText')}
							</Typography>
						</>
					)}
				</Stack>
				<Button variant="outlined" onClick={handleClose}>
					{intl.tr('domainVerification.dialog.close')}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default VerifyDomainDialog;
